<template>
  <div class="meetings-breadcrumbs mt-16 laptop:mt-0" data-testid="page-meetings">
    <Breadcrumbs :items="breadcrumbItems" />
  </div>
  <div class="min-h-screen w-screen max-w-[1200px] bg-surface-elevation-1 px-8 py-8 laptop:mx-auto laptop:w-full">
    <ContentCard>
      <div class="p-6">
        <CircleSpinner v-if="loading" />
        <template v-else>
          <div class="flex items-center justify-between">
            <TabBar>
              <NavTabBarItem
                :to="{ name: 'meetingsMyMeetings' }"
                :is-selected="route.name === 'meetingsMyMeetings'"
                :disabled="!isMeetingsEnabled"
              >
                {{ t('Meetings.tabs.myMeetings') }}
                <span v-if="approvedCount > 0" class="text-pink-600 ml-1">{{ approvedCount }}</span>
              </NavTabBarItem>
              <NavTabBarItem
                :to="{ name: 'meetingsRequests' }"
                :is-selected="route.name === 'meetingsRequests'"
                :disabled="!isMeetingsEnabled"
              >
                {{ t('Meetings.tabs.requests') }}
                <span v-if="pendingCount > 0" class="text-pink-600 ml-1">{{ pendingCount }}</span>
              </NavTabBarItem>
              <NavTabBarItem
                :to="{ name: 'meetingsDeclined' }"
                :is-selected="route.name === 'meetingsDeclined'"
                :disabled="!isMeetingsEnabled"
              >
                {{ t('Meetings.tabs.declined') }}
                <span v-if="declinedCount > 0" class="text-pink-600 ml-1">{{ declinedCount }}</span>
              </NavTabBarItem>
              <NavTabBarItem :to="{ name: 'meetingsConfiguration' }" :is-selected="route.name === 'meetingsConfiguration'">
                {{ t('Meetings.tabs.configuration') }}
              </NavTabBarItem>
            </TabBar>
            <div id="meetings-tab-actions" />
          </div>
          <HorizontalDivider class="-mt-0.25" />
        </template>
        <router-view v-show="!loading" />
      </div>
    </ContentCard>

    <ToastMsg
      :visible="toastVisible"
      :type="toastType"
      :timeout="5000"
      class="fixed bottom-4 left-1/2 z-50 max-w-md -translate-x-1/2"
      @hide="toastVisible = false"
    >
      {{ toastMessage }}
    </ToastMsg>
  </div>
</template>

<script setup lang="ts">
import { Breadcrumbs, CircleSpinner, ToastMsg } from '@ramp106/omrjs-core-ui'
import { computed, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { ROOT_EVENT_SLUG } from '@/components/VisitorProfiles/config'
import { AppointmentStateEnum, useMeetingsAppointmentsQuery, useMeetingsIsEnabledQuery } from '@/gql/myomr'
import { useRoleRedirect } from '@/services/roleRedirect'
import ContentCard from '@/ui/ContentCard.vue'
import HorizontalDivider from '@/ui/HorizontalDivider.vue'
import { TabBar, NavTabBarItem } from '@/ui/Tabs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

useRoleRedirect('viewMeetings')

const { result, loading, refetch } = useMeetingsIsEnabledQuery({
  eventSlug: ROOT_EVENT_SLUG,
})

const isMeetingsEnabled = computed(() => result.value?.meetings.isEnabled)

provide('meetingsEnabled', isMeetingsEnabled)
provide('meetingsRefetch', refetch)

// Redirect to configuration when meetings not enabled and user is on a disabled tab
watch(
  [isMeetingsEnabled, () => route.name],
  ([enabled, routeName]) => {
    if (enabled === false && ['meetingsMyMeetings', 'meetingsRequests', 'meetingsDeclined'].includes(routeName as string)) {
      router.replace({ name: 'meetingsConfiguration' })
    }
  },
  { immediate: true },
)

// Query for approved appointments count
const { result: approvedResult, refetch: refetchApproved } = useMeetingsAppointmentsQuery({
  eventSlug: ROOT_EVENT_SLUG,
  states: [AppointmentStateEnum.Approved],
})

// Query for pending appointments count
const { result: pendingResult, refetch: refetchPending } = useMeetingsAppointmentsQuery({
  eventSlug: ROOT_EVENT_SLUG,
  states: [AppointmentStateEnum.Created],
})

// Query for declined appointments count
const { result: declinedResult, refetch: refetchDeclined } = useMeetingsAppointmentsQuery({
  eventSlug: ROOT_EVENT_SLUG,
  states: [AppointmentStateEnum.Denied, AppointmentStateEnum.Cancelled, AppointmentStateEnum.DeclinedAuto],
})

function refetchCounters() {
  refetchApproved()
  refetchPending()
  refetchDeclined()
}

provide('refetchCounters', refetchCounters)

// Toast notifications
const toastVisible = ref(false)
const toastMessage = ref('')
const toastType = ref<'success' | 'error'>('success')

function showToast(message: string, type: 'success' | 'error') {
  toastVisible.value = false
  // Reset visibility to retrigger the toast if already showing
  setTimeout(() => {
    toastMessage.value = message
    toastType.value = type
    toastVisible.value = true
  }, 0)
}

provide('showToast', showToast)

const breadcrumbItems = [{ text: t('SideBar.leads'), to: { path: '/networking' }, nuxt: true }, { text: t('Meetings.title') }]

const approvedCount = computed(() => approvedResult.value?.meetings?.appointments?.pageInfo?.totalCount ?? 0)
const pendingCount = computed(() => pendingResult.value?.meetings?.appointments?.pageInfo?.totalCount ?? 0)
const declinedCount = computed(() => declinedResult.value?.meetings?.appointments?.pageInfo?.totalCount ?? 0)
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.meetings-breadcrumbs :deep(.breadcrumbs) {
  @apply border-b border-main bg-surface px-6 py-2;
}

.meetings-breadcrumbs :deep(nav) {
  @apply flex min-h-[41px] items-center gap-2 border-y-0 py-0 text-headline-xs;
}

.meetings-breadcrumbs :deep(a) {
  @apply btn-link-neutral-m txt-headline-m-base font-normal text-main! no-underline hover:underline;
}

.meetings-breadcrumbs :deep(li:last-child span) {
  @apply font-bold text-main;
}

.meetings-breadcrumbs :deep(.text-body-s) {
  font-size: inherit;
}
</style>
